import type { BatchStatus } from '../../types/batch-retry.api.types';
import {
  DEFAULT_MAX_ATTEMPTS,
  type JourneyClass,
  type JourneyRun,
  type PostStepHealth,
  type PostStepName,
  type PostSteps,
} from '../../types/post-step-retry.api.types';

/** The four stepper steps; Enrichment is today's Runs UI. */
export type StepKey = 'ENRICHMENT' | 'SEARCH_DESCRIPTORS' | 'PHRASE_DESCRIPTIONS' | 'DELIVERY';

export const STEP_NAME: Record<StepKey, string> = {
  ENRICHMENT: 'Enrichment',
  SEARCH_DESCRIPTORS: 'Search descriptors',
  PHRASE_DESCRIPTIONS: 'Phrase descriptions',
  DELIVERY: 'Delivery',
};

/** Tone of a class chip: ok = healthy, bad = a retry may fix it, fix = needs a data fix first. */
export type ClassTone = 'ok' | 'bad' | 'fix' | 'info';

export interface ClassDef {
  key: JourneyClass;
  label: string;
  tone: ClassTone;
  /** One-line plain-language explanation shown under the pills. */
  explain: string;
}

const SD_CLASSES: ClassDef[] = [
  {
    key: 'COMPLETED',
    label: 'Phrases ready',
    tone: 'ok',
    explain: 'Search phrases were generated and will be added to the description.',
  },
  {
    key: 'FAILED',
    label: 'Agent error',
    tone: 'bad',
    explain:
      'The phrase agent timed out or the keyword index was unavailable. Retry fixes most of these.',
  },
  {
    key: 'MISSING',
    label: 'No result',
    tone: 'bad',
    explain: 'The product was enriched but no phrase result came back. Retry fixes these.',
  },
  {
    key: 'NO_COVERAGE',
    label: 'No keywords',
    tone: 'fix',
    explain:
      'Your keyword index has nothing for this product type. Add keywords for it, then retry.',
  },
];

const PD_CLASSES: ClassDef[] = [
  {
    key: 'COMPLETED',
    label: 'Descriptions ready',
    tone: 'ok',
    explain: 'All three description versions were built from the phrases.',
  },
  {
    key: 'PARTIAL',
    label: 'Partly ready',
    tone: 'bad',
    explain: 'Versions 1 and 2 are ready; version 3 failed. Retry rebuilds only version 3.',
  },
  {
    key: 'FAILED',
    label: 'Agent error',
    tone: 'bad',
    explain: 'The description agent errored. Retry fixes most of these.',
  },
  {
    key: 'MISSING',
    label: 'No result',
    tone: 'bad',
    explain: 'The product was enriched but no description result came back. Retry fixes these.',
  },
  {
    key: 'WAITING',
    label: 'Waiting for phrases',
    tone: 'info',
    explain:
      'These need search phrases first. Retrying search descriptors rebuilds them in the same run.',
  },
];

const DELIVERY_CLASSES: ClassDef[] = [
  {
    key: 'READY_TO_REDELIVER',
    label: 'Ready to redeliver',
    tone: 'fix',
    explain:
      'Fixed by this retry after the batch was delivered. Google keeps the old version until you redeliver.',
  },
  {
    key: 'REDELIVERED',
    label: 'Redelivered',
    tone: 'ok',
    explain: 'Sent to Google again after the retry fixed them.',
  },
  {
    key: 'DELIVERED',
    label: 'Delivered',
    tone: 'ok',
    explain: 'Sent to Google in the batch delivery.',
  },
  {
    key: 'IN_BATCH_DELIVERY',
    label: 'In the batch delivery',
    tone: 'info',
    explain:
      'Sent with the batch delivery. Some products of it failed; Delivery Health shows which.',
  },
];

/** Shown only while a retry runs. */
const PENDING_CLASS: ClassDef = {
  key: 'PENDING',
  label: 'Queued',
  tone: 'info',
  explain: 'Running now. Counts update as products finish.',
};

/** Classes of a step, healthy one first. Enrichment has none: it keeps today's grid. */
export function classesFor(step: StepKey): ClassDef[] {
  switch (step) {
    case 'SEARCH_DESCRIPTORS':
      return [...SD_CLASSES, PENDING_CLASS];
    case 'PHRASE_DESCRIPTIONS':
      return [...PD_CLASSES, PENDING_CLASS];
    case 'DELIVERY':
      return DELIVERY_CLASSES;
    default:
      return [];
  }
}

export function classLabel(step: StepKey, cls: string | null | undefined): string {
  if (!cls) return '—';
  return classesFor(step).find((c) => c.key === cls)?.label ?? cls;
}

/** Enrichment states a journey `before` can carry on an enrichment run. */
const ENRICHMENT_BEFORE: Record<string, string> = {
  NOT_REACHED: 'Not reached',
  FAILED: 'Enrichment failed',
  ENRICHED: 'Enriched in this run',
};

/** How the batch delivery sent a product a retry fixed later. */
const DELIVERY_BEFORE: Record<string, string> = {
  DELIVERED: 'Delivered before the fix',
  IN_BATCH_DELIVERY: 'Sent before the fix',
};

/**
 * "Before this run" cell. Enrichment runs report the product's enrichment state; a post-step
 * retry reports the step class it started from (FAILED there means the step failed); a
 * redelivery reports how the batch delivery sent the product.
 */
export function beforeLabel(step: StepKey, run: JourneyRun | null, before: string | null): string {
  if (!before) return '—';
  if (step === 'DELIVERY' && DELIVERY_BEFORE[before]) return DELIVERY_BEFORE[before];
  if (run?.kind !== 'POST_STEP_RETRY' && ENRICHMENT_BEFORE[before])
    return ENRICHMENT_BEFORE[before];
  return classLabel(step, before);
}

/**
 * Products a retry would pick: the server's `toFix` (unhealthy, not accepted, not given up); an
 * older backend without it falls back to every unhealthy class except waiting / pending.
 */
export function toFixOf(step: PostStepName, health: PostStepHealth | null | undefined): number {
  if (!health) return 0;
  if (typeof health.toFix === 'number') return health.toFix;
  const extra = step === 'SEARCH_DESCRIPTORS' ? (health.noCoverage ?? 0) : (health.partial ?? 0);
  return health.failed + health.missing + extra;
}

export function healthOf(
  postSteps: PostSteps | null | undefined,
  step: PostStepName,
): PostStepHealth | null {
  const block =
    step === 'SEARCH_DESCRIPTORS' ? postSteps?.searchDescriptors : postSteps?.phraseDescriptions;
  return block ?? null;
}

/** Every step, in order: the stepper always shows all four. */
export const JOURNEY_STEPS: readonly StepKey[] = [
  'ENRICHMENT',
  'SEARCH_DESCRIPTORS',
  'PHRASE_DESCRIPTIONS',
  'DELIVERY',
];

/** Whether the batch ran the step: SD / PD only when the run requested them, the rest always. */
export function isStepEnabled(postSteps: PostSteps | null | undefined, step: StepKey): boolean {
  if (step === 'SEARCH_DESCRIPTORS') return postSteps?.searchDescriptors != null;
  if (step === 'PHRASE_DESCRIPTIONS') return postSteps?.phraseDescriptions != null;
  return true;
}

export const CLASS_TONE: Record<ClassTone, string> = {
  ok: 'bg-emerald-50 text-emerald-700',
  bad: 'bg-error-50 text-error',
  fix: 'bg-warning-50 text-warning',
  info: 'bg-muted text-muted-foreground',
};

/** What a post-step retry's redelivery is doing, as a card chip. */
export function redeliveryLabel(run: JourneyRun): string {
  const status = run.redelivery?.status ?? null;
  if (status === null) return 'Ready to redeliver';
  if (status === 'RUNNING') return 'Sending…';
  if (status === 'COMPLETED' || status === 'MOCK' || status === 'SUPPRESSED') return 'Redelivered';
  return 'Redelivery failed';
}

/**
 * SD retries also rebuild the phrase descriptions that depend on them — but only when the run
 * produced phrase descriptions at all (asking for a step the run never ran is a 400). PD retries
 * only PD (the server runs SD first where PD needs it).
 */
export function stepsFor(
  step: PostStepName,
  postSteps: PostSteps | null | undefined,
): PostStepName[] {
  if (step === 'PHRASE_DESCRIPTIONS') return [step];
  return postSteps?.phraseDescriptions
    ? ['SEARCH_DESCRIPTORS', 'PHRASE_DESCRIPTIONS']
    : ['SEARCH_DESCRIPTORS'];
}

/** Failed retries after which a product "gave up": the server's, else its default. */
export function maxAttemptsOf(postSteps: PostSteps | null | undefined): number {
  return postSteps?.maxAttemptsPerProduct && postSteps.maxAttemptsPerProduct > 0
    ? postSteps.maxAttemptsPerProduct
    : DEFAULT_MAX_ATTEMPTS;
}

export interface StepAttention {
  toFix: number;
  gaveUp: number;
  accepted: number;
  needsKeywords: number;
  waiting: number;
}

/** The step's safeguard partition (zeros on an older backend, `toFix` then falls back). */
export function attentionOf(
  step: PostStepName,
  health: PostStepHealth | null | undefined,
): StepAttention {
  return {
    toFix: toFixOf(step, health),
    gaveUp: health?.gaveUp ?? 0,
    accepted: health?.accepted ?? 0,
    needsKeywords: health?.noCoverageRetried ?? 0,
    waiting: health?.waiting ?? 0,
  };
}

/** Live classes a person can accept as is: the unhealthy ones a retry acts on. */
export function isAcceptable(cls: JourneyClass | null | undefined): boolean {
  return cls === 'FAILED' || cls === 'MISSING' || cls === 'NO_COVERAGE' || cls === 'PARTIAL';
}

/**
 * Why a batch's Delivery view has no delivery to show; "FAILED" must never read as awaiting
 * approval. RETRYING / PAUSED say nothing about delivery (the batch may have delivered before a
 * retry or pause), so they get their own neutral reason rather than "not delivered".
 */
export type UndeliveredReason = 'AWAITING_APPROVAL' | 'FAILED' | 'RETRYING' | 'PAUSED' | 'PENDING';

export function undeliveredReason(status: BatchStatus | null | undefined): UndeliveredReason {
  if (status === 'AWAITING_APPROVAL') return 'AWAITING_APPROVAL';
  if (status === 'FAILED') return 'FAILED';
  if (status === 'RETRYING') return 'RETRYING';
  if (status === 'PAUSED') return 'PAUSED';
  return 'PENDING';
}
